import axios from 'axios/dist/axios';
import Bowser from "bowser";


const assessmentStatuses = {
    PRODUCT_LIST: "PRODUCT_LIST",
    START_QUESTIONS: "START_QUESTIONS",
    GENERAL_QUESTIONS: "GENERAL_QUESTIONS",
    CHAPTER_TREE: "CHAPTER_TREE",
    RELATED_SYMPTOMS: "RELATED_SYMPTOMS",
    NO_OTHER_SYMPTOMS: "NO_OTHER_SYMPTOMS",
    SPECIFIC_QUESTIONS: "SPECIFIC_QUESTIONS",
    SHARED_QUESTIONS: "SHARED_QUESTIONS",
    EVALUATED: "EVALUATED",
    ASSESSMENT_STOPPED: "ASSESSMENT_STOPPED",

    // Statuses for "study" application types:
    FIRST_NAME: "FIRST_NAME",
    LAST_NAME: "LAST_NAME",
    BIRTH_DATE: "BIRTH_DATE"
};

const conversationMessageTypes = {
    SENT: "SENT",
    RECEIVED: "RECEIVED"
};

const noOtherSymptomAnswers = {
    YES: "YES",
    NO: "NO"
};

const relatedSymptomStates = {
    NONE: "NONE", //The Symptom is not selected
    HAVE_SYMPTOM: "HAVE_SYMPTOM", //The Symptom is selected. The person has that Symptom
};

const pathDrSmassPersonas = "/pfi/personas/";

const staticPathDrSmassPersonas = "static/img/personas/";

/**
 * Contexts are used with API requests. They give the API some information and the API will know what information to send back on response.
 * @type {{ASSESSMENT_PFI: string, EDIT: string, NEXT_ENTRY: string}}
 */
const contexts = {

    // "ASSESSMENT_PFI" tells the API to respond with conversationStatus (next assessment status) & conversation data
    // (message list data). So NEXT_ENTRY (conversationStatus) is automatically included in the response.
    // This has to be sent as API request parameter when we need complete assessment data (ex. on page refresh).
    ASSESSMENT_PFI: "ASSESSMENT_PFI",

    // "EDIT" has to be sent as API request param when editing an answer. It includes ASSESSNENT_PFI (which includes NEXT_ENTRY).
    // When this is passed to API, the API will give the same response like when ASSESSMENT_PFI is passed,
    // but on the server side will use the information that an answer edit has been submitted.
    EDIT: "EDIT",

    // "NEXT_ENYTRY" tells the API to respond with conversationStatus (next assessment status).
    // It's similar to ASSESSMENT_PFI without conversation list. So it's like ASSESSMENT_PFI,
    // but it misses the conversation data (message list data).
    // This must be sent as API request param when an answer is submitted, because we need the server to tell us
    // where to go next, for example we need the server to send us the next question and to tell us
    // to which conversationStatus it belongs.
    NEXT_ENTRY: "NEXT_ENTRY"
};

const entityTypes = {
    MODULE: "MODULE",
    QUESTION: "QUESTION",
    ANSWER: "ANSWER",
    SYMPTOM: "SYMPTOM",
    RELATED_SYMPTOM: "RELATED_SYMPTOM",
    NO_OTHER_SYMPTOM: "NO_OTHER_SYMPTOM"
};


// ======================================================================
// Default State Values
// ======================================================================

const defaultState = {

    currentAssessmentStatus: "PRODUCT_LIST",
    conversationMessages: [
        /**
         * {
         *    // --- Required properties: ---
         *    text: "Lorem ipsum?",
         *    type: "SENT", // or "RECEIVED"
         *    // --- optional properties: ---
         *    questionCode: "gaj348oith84uaelefrg",
         *    answerCode: "fj84ulijhklfgs",
         *    mainSymptomCode: "8493tuierhlerfjgk",
         *    relatedSymptomsCodes: ["ar9p8tg489tuif", "784iulgjkhfdgp", "7rauh48gisdjklf"]
         * }
         */
    ],
    currentQuestion: null,

    assessmentToken: null,
    assessmentVersion: null,
    productList: null,
    chapterTree: null,
    searchSymptomResultList: [],

    relatedSymptomList: null,
    evaluationData: null,
    clickedProduct: null,
    goToLastName: "",
    goToBirthDate: "",
    goToGeneralQuestions: "",
    endAssessment: false,
    assessmentStarted: false,
    mustConfirmNoOtherSymptom: false,
    moreContentAvailable: false,
    drSmassPersonas: null,

    // Edit answer
    mustShowEditAnswerPopup: false,
    mustShowChooseMainSymptomPopup: false,
    crtEditingQuestionData: null,

    isEditAction: false,
    mustShowAssessmentNoAvailablePopup: false,
    docPopupData: null,

};

// ======================================================================
// Initial State
// ======================================================================

const state = {
    currentAssessmentStatus:        defaultState.currentAssessmentStatus,
    conversationMessages:           defaultState.conversationMessages,
    currentQuestion:                defaultState.currentQuestion,
    assessmentToken:                defaultState.assessmentToken,
    assessmentVersion:              defaultState.assessmentVersion,
    productList:                    defaultState.productList,
    chapterTree:                    defaultState.chapterTree,
    searchSymptomResultList:        defaultState.searchSymptomResultList,
    relatedSymptomList:             defaultState.relatedSymptomList,
    evaluationData:                 defaultState.evaluationData,
    patientFirstName:               defaultState.patientFirstName,
    patientLastName:                defaultState.patientLastName,
    patientBirthDate:               defaultState.patientBirthDate,
    clickedProduct:                 defaultState.clickedProduct,
    goToLastName:                   defaultState.goToLastName,
    goToBirthDate:                  defaultState.goToBirthDate,
    goToGeneralQuestions:           defaultState.goToGeneralQuestions, // @TODO This seems to be used only for STUDY appType, in AnswerBirthDate section. We have to check if we need it anymore.
    endAssessment:                  defaultState.endAssessment,
    assessmentStarted:              defaultState.assessmentStarted,
    mustConfirmNoOtherSymptom:      defaultState.mustConfirmNoOtherSymptom,
    moreContentAvailable:           defaultState.moreContentAvailable,
    drSmassPersonas:                defaultState.drSmassPersonas,
    chatBotScoringData:             defaultState.chatBotScoringData,
    // Edit answer
    mustShowEditAnswerPopup:        defaultState.mustShowEditAnswerPopup,
    mustShowChooseMainSymptomPopup: defaultState.mustShowChooseMainSymptomPopup,
    crtEditingQuestionData:         defaultState.crtEditingQuestionData,

    isEditAction:                   defaultState.isEditAction,
    mustShowAssessmentNoAvailablePopup: defaultState.mustShowAssessmentNoAvailablePopup,
    docPopupData:                   defaultState.docPopupData,
};


// ======================================================================
// Getters
// ======================================================================

const getters = {

    getCurrentAssessmentStatus: state => state.currentAssessmentStatus,
    getConversationMessages: state => state.conversationMessages,
    getCurrentQuestion: state => state.currentQuestion,
    getAssessmentToken: state => state.assessmentToken,
    getAssessmentVersion: state => state.assessmentVersion,
    getProductList: state => state.productList,
    getChapterTree: state => state.chapterTree,
    getSearchSymptomResultList: state => state.searchSymptomResultList,
    getRelatedSymptomList: state => state.relatedSymptomList,
    getEvaluationData: state => state.evaluationData,

    getNoOtherSymptomAnswers: () => noOtherSymptomAnswers,
    getConversationMessageTypes: () => conversationMessageTypes,
    getRelatedSymptomStates: () => relatedSymptomStates,
    getAssessmentStatuses: () => assessmentStatuses,
    getEntityTypes: () => entityTypes,
    getPatientFirstName: state => state.patientFirstName,
    getPatientLastName: state => state.patientLastName,
    getPatientBirthDate: state => state.patientBirthDate,
    getClickedProduct: state => state.clickedProduct,
    getSelectedProductCode: state => {
        return (state.clickedProduct && state.clickedProduct.code) ? state.clickedProduct.code : null;
    },
    getGoToLastName:  state => state.goToLastName,
    getGoToBirthDate: state => state.goToBirthDate,
    getGoToGeneralQuestions: state => state.goToGeneralQuestions,
    getEndAssessment: state => state.endAssessment,
    getAssessmentStarted: state => state.assessmentStarted,
    getMustConfirmNoOtherSymptom: state => state.mustConfirmNoOtherSymptom,
    getMoreContentAvailable: state => state.moreContentAvailable,
    getDrSmassPersonas: state => state.drSmassPersonas,
    getChatBotScoringData: state => state.chatBotScoringData,
    // Edit answer
    getMustShowEditAnswerPopup: state => state.mustShowEditAnswerPopup,
    getMustShowChooseMainSymptomPopup: state => state.mustShowChooseMainSymptomPopup,
    getCrtEditingQuestionData: state => state.crtEditingQuestionData,
    getConfirmedMainSymptomCode: (state)  => {
        return (typeof state.chapterTree.mainSymptomCode !== "undefined") ? state.chapterTree.mainSymptomCode : null;
    },
    getConfirmedMainSymptom: (state, getters)  => {

        if (getters.getConfirmedMainSymptomCode === null || getters.chapterTree === null) {
            return null;
        }
        else {
            let mainSymptomData = null;
            let chapter;
            let subchapter;
            let mainSymptomObj;

            if (getters.getChapterTree.hasSubChapters === true) {
                // --- Has subchapters. ---
                chapter = getters.getChapterTree.chapters.find((chapter) => {
                    subchapter = chapter.subChapters.find((subchapter) => {
                        mainSymptomObj = subchapter.symptoms.find((symptom) => {
                            return (symptom.code === getters.getConfirmedMainSymptomCode) ? true : false;
                        });

                        return (typeof mainSymptomObj !== "undefined") ? true : false;
                    });
                    return (typeof subchapter !== "undefined") ? true : false;
                });

                mainSymptomData = {...mainSymptomObj};
                mainSymptomData.chapterCode = (typeof chapter !== "undefined") ? chapter.code : null;
                mainSymptomData.subchapterCode = (typeof subchapter !== "undefined") ? subchapter.code : null;
            }
            else {
                // --- Has no subchapters. ---
                chapter = getters.getChapterTree.chapters.find((chapter) => {
                    mainSymptomObj = chapter.symptoms.find((symptom) => {
                        return (symptom.code === getters.getConfirmedMainSymptomCode) ? true : false;
                    });
                    return (typeof mainSymptomObj !== "undefined") ? true : false;
                });

                mainSymptomData = {...mainSymptomObj};
                mainSymptomData.chapterCode = (typeof chapter !== "undefined") ? chapter.code : null;
                mainSymptomData.subchapterCode = null;
            }

            return mainSymptomData;
        }

    },

    getIsEditAction: state => state.isEditAction,
    getMustShowAssessmentNoAvailablePopup: state => state.mustShowAssessmentNoAvailablePopup,
    getDocPopupData: state => state.docPopupData
};


// ======================================================================
// Actions
// ======================================================================

const actions = {

    loadProductList ({ dispatch, commit, rootGetters}) {
        let promise = new Promise((resolve, reject) => {
            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/productList');
            axiosRequest.then((response) => {
                if (typeof response.data.products !== "undefined") {

                    // Add question to conversation list.
                    if (response.data.products.length > 1) {
                        commit("appendConversationMessage", {
                            text: this._vm.$text("api.chatbot.conversationMsg.selectProduct"), // "Hi again, please select a product to start the evaluation.",
                            type: conversationMessageTypes.RECEIVED
                        });
                    }
                    else {
                        commit("appendConversationMessage", {
                            text: this._vm.$text("api.chatbot.conversationMsg.startAssessment"), // "Please start the assessment when you are ready",
                            type: conversationMessageTypes.RECEIVED
                        });
                    }

                    // Set product list.
                    commit('setProductList', response.data.products);

                    resolve(response);
                } else {
                    reject("Invalid or missing product list data.");
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });


        return promise;
    },

    selectProduct ({commit, dispatch, rootState}, productData) {

        let promise = new Promise((resolve, reject) => {

            commit("setClickedProduct", productData);

            commit("clearProductList");
            commit("clearConversationMessages");

            resolve();
        });

        return promise;
    },

    loadAssessmentStartData ({ dispatch, commit, rootGetters, getters, rootState }, productCode) {

        let promise = new Promise((resolve, reject) => {

            let axiosRequest;

            /*** IF appType === 'PF_CHATBOT_STUDY'  display PATIENT QUESTIONS (patient name, patient birthdate) ***/
            let parameters = {};
            parameters.theme = rootGetters['ThemeStore/getTheme'];

            const browser = Bowser.getParser(window.navigator.userAgent);

            parameters.browserName = browser.getBrowserName();
            parameters.browserVersion = browser.getBrowserVersion();
            parameters.browserOS = browser.getOSName() + " " + browser.getOSVersion();

            if (rootState.TranslationStore.appTypeName === "PF_CHATBOT_STUDY") {
                axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/startQuestionsNextQ', {
                    "productCode": productCode,
                    "context": [contexts.NEXT_ENTRY],
                    "parameters": parameters
                });
            }
            else {
                axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/startAssessmentNextQ', {
                    "productCode": productCode,
                    "context": [contexts.NEXT_ENTRY],
                    "parameters": parameters
                });
            }

            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    commit('setAssessmentStarted', true); // @TODO Make sure that we cannot get rid of "assessmentStarted" property.

                    if (typeof response.data.data !== "undefined" && typeof response.data.data.APP_PROPERTIES !== "undefined" && typeof response.data.data.APP_PROPERTIES.appProperties !== "undefined") {
                        commit("TranslationStore/setAppProperties", response.data.data.APP_PROPERTIES.appProperties, {root: true});
                    }

                    if (typeof response.data.data.PRODUCT_TEXTS !== "undefined" && typeof response.data.data.PRODUCT_TEXTS.productTextMap !== "undefined") {
                        dispatch('TranslationStore/appendProductRelatedTranslationData', response.data.data.PRODUCT_TEXTS.productTextMap, { root: true });
                    }

                    if (response.data.assessment && response.data.assessment.assessmentToken) {
                        commit('setAssessmentToken', response.data.assessment.assessmentToken);
                    }

                    let mustRefreshConversation = true;
                    dispatch("goToNextQuestion", {response, mustRefreshConversation}).then(() => {
                        resolve(response);
                    });
                }
                else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });

        });

        return promise;
    },

    submitDataContainer({commit, dispatch, rootState}, dc) {

        let promise = new Promise((resolve, reject) => {

            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/setDataContainer', {
                value: dc
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },


    // @TODO | Maybe we can DELETE this function and as a replacement for it create
    // @TODO | section-specific sections that set to the store the data received
    // @TODO | (on ASSESSMENT_PFI key) as xhr response when previous answer has been submitted.
    beginSection ({commit, dispatch}, {section, payload}) {

        commit("setCurrentAssessmentStatus", section);

        // Load initial data (i.e. first question) for a new section.
        switch (section) {
            case assessmentStatuses.PRODUCT_LIST:
                // @TODO Load product list? Do we need it here?
                break;

            case assessmentStatuses.START_QUESTIONS:
                dispatch("loadAssessmentStartData", payload.productCode);
                break;

            case assessmentStatuses.GENERAL_QUESTIONS:
                dispatch("loadFirstGeneralQuestion");
                break;

            case assessmentStatuses.CHAPTER_TREE:
                commit("appendConversationMessage", {
                    text: this._vm.$text("api.chatbot.conversationMsg.selectMainSymptom"), // "What are you suffering from the most right now? Please select the main symptom.",
                    type: conversationMessageTypes.RECEIVED,
                });
                commit("appendConversationMessage", {
                    text: this._vm.$text("api.chatbot.conversationMsg.searchOrSelectMainSymptom"), // "You can type the symptom or search it manually.",
                    type: conversationMessageTypes.RECEIVED,
                });
                if (typeof payload.chapterTreeData !== "undefined") {
                    commit("setChapterTree", payload.chapterTreeData);
                }
                else {
                    dispatch("loadChapterTree");
                }
                break;

            case assessmentStatuses.RELATED_SYMPTOMS:
                let that = this;

                let promiseLoadRelatedSymptomList = dispatch("loadRelatedSymptomList");

                promiseLoadRelatedSymptomList.then(() => {
                    if(state.relatedSymptomList !== null && state.relatedSymptomList.length > 0) {
                        commit("appendConversationMessage", {
                            text: that._vm.$text("api.chatbot.conversationMsg.selectRelatedSymptoms"), // "Select additional symptoms.",
                            type: conversationMessageTypes.RECEIVED,
                        });
                    } else {

                        if (state.mustConfirmNoOtherSymptom) {
                            dispatch("beginSection", {
                                section: assessmentStatuses.NO_OTHER_SYMPTOMS
                            });
                        }
                        else {
                            dispatch("beginSection", {
                                section: assessmentStatuses.SPECIFIC_QUESTIONS
                            });
                        }
                    }
                });
                break;

            case assessmentStatuses.NO_OTHER_SYMPTOMS:
                commit("setCurrentAssessmentStatus", assessmentStatuses.NO_OTHER_SYMPTOMS);
                commit("appendConversationMessage", {
                    text: this._vm.$text("api.chatbot.conversationMsg.noOtherSymptomsQuestion"), // "Do you have any other symptoms?...",
                    type: conversationMessageTypes.RECEIVED,
                });
                // Clear related symptoms.
                commit("clearRelatedSymptomList");
                break;

            case assessmentStatuses.SPECIFIC_QUESTIONS:
                dispatch("loadFirstSpecificQuestionOfASymptom");
                break;

            case assessmentStatuses.SHARED_QUESTIONS:
                dispatch("loadFirstSharedQuestion");
                break;

            case assessmentStatuses.EVALUATED:
                break;

            case assessmentStatuses.FIRST_NAME:
                dispatch("enterPatientFirstName", payload.productCode);
                break;

            case assessmentStatuses.LAST_NAME:
                dispatch("enterPatientLastName");
                break;

            case assessmentStatuses.BIRTH_DATE:
                dispatch("enterPatientBirthDate");
                break;

            default:
                // Do nothing.
        }
    },




    /**
     * Handle question's answer selection.
     * @param {Object} - Vuex: commit, dispatch
     * @param {Object} data - Object containing the following properties: "answer", "question".
     * @return {Promise}
     */
    selectAnswer({commit, dispatch, getters, rootState}, data) {

        dispatch("submitAnswer", {
            questionCode: data.question.code,
            answerCode: data.answer.code
        }).then((response) => {

            // Add response to conversation list.
            commit("appendConversationMessage", {
                text: data.answer.text,
                type: conversationMessageTypes.SENT,
                // optional properties:
                questionCode: data.question.code,
                answerCode: data.answer.code,
                entityType: entityTypes.ANSWER
            });

            if (typeof response.data.data !== "undefined" && typeof response.data.data.CALLBACK_DATA !== "undefined") {
                commit("TranslationStore/setVoucherCallbackData", response.data.data.CALLBACK_DATA, {root: true});
            }

            // iF CHATBOT STUDY-urgent popup is not displayed, instead text "Thanks for the participation" will be displayed
            if (state.endAssessment === true && (rootState.TranslationStore.appTypeName === "PF_CHATBOT_STUDY" || rootState.TranslationStore.appTypeName === "PF_CHATBOT_PUBLIC"
                    || rootState.TranslationStore.appTypeName === null || rootState.TranslationStore.appTypeName === "PF_CHATBOT")) {

                if (rootState.TranslationStore.appTypeName === "PF_CHATBOT_STUDY"  ) {
                    commit("appendConversationMessage", {
                        text: this._vm.$text("api.chatbot.conversationMsg.thanksForTheParticipation"), // "Thanks for the participation",
                        type: conversationMessageTypes.RECEIVED
                    });
                }

                // For PF_CHATBOT_PUBLIC display the UrgentPopup
                if (rootState.TranslationStore.appTypeName === "PF_CHATBOT_PUBLIC" || rootState.TranslationStore.appTypeName === null || rootState.TranslationStore.appTypeName === "PF_CHATBOT") {
                    dispatch("AssessmentAlertsStore/refreshAlerts", response.data, {root: true});
                }

                dispatch("beginSection", {
                    section: assessmentStatuses.EVALUATED
                });
            }

            commit("clearCurrentQuestion");

            setTimeout(() => { // @TODO This setTimeout is a workaround to animate the new question when answer animation is finished. Need to find a better way, it's not the best idea to handle the animation from the store.

                dispatch("goToNextQuestion", {response});
            }, 0); // TODO Review if is still needed this setTimeout(). I hope we can remove it.
        });
    },


    /**
     * Send question's answer to the server.
     * @param {Object} - Vuex: commit, dispatch
     * @param {Object} response - Object containing request's response.
     * @return {Promise}
     */
    submitAnswer({commit, dispatch}, data) {

        let promise = new Promise((resolve, reject) => {

            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/provideAnswerNextQ', {
                "questionCode": data.questionCode,
                "answerCode": data.answerCode,
                "context": [contexts.NEXT_ENTRY]
            });
            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {
                    if (response.data.assessment.urgentData) {
                        commit("setEndAssessment", true);
                    }

                    resolve(response);
                }
                else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },




    // #######################################################
    //  Begin CHOOSE MAIN SYMPTOM (Chapter Tree)
    // #######################################################

    /**
     * @TODO Do we need this function anymore? Isn't the data coming from ASSESSMENT_PFI instead? Or maybe we still need this for Edit Main Symptom popup?
     *
     * (Re)Load chapter tree data for "Choose Main Symptom" popup.
     * @param {Object} - Vuex: commit
     * @param {Object} payload (Optional) - Additional data. If "edit" property is present and TRUE, it have to be added to chapterTree object that comes from API.
     * @return {Promise}
     */
    loadChapterTree({commit}, payload) {
        let promise = new Promise((resolve, reject) => {

            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/chapterTree');
            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    if (typeof response.data.data !== "undefined"
                            && response.data.data !== null
                            && typeof response.data.data.CHAPTER_TREE !== "undefined"
                            && response.data.data.CHAPTER_TREE !== null) {

                        commit("setChapterTree", response.data.data.CHAPTER_TREE);

                        resolve(response);
                    }
                    else {
                        reject("Invalid or missing chapter tree data.");
                    }
                } else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },


    submitSearchMainSymptom({commit, dispatch, getters}, {searchedText}) {
        let promise = new Promise((resolve, reject) => {

            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/searchSymptom', {
                "searchText": searchedText
            });
            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    if (typeof response.data.data !== "undefined"
                            && response.data.data !== null
                            && typeof response.data.data.SEARCHED_SYMPTOMS !== "undefined"
                            && response.data.data.SEARCHED_SYMPTOMS !== null
                            && typeof response.data.data.SEARCHED_SYMPTOMS.symptoms !== "undefined") {

                        commit("setSearchSymptomResultList", response.data.data.SEARCHED_SYMPTOMS.symptoms);

                        resolve(response);
                    } else {
                        reject("Invalid or missing search symptoms results data.");
                    }
                } else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },


    chooseMainSymptom({commit, dispatch}, payload) {
        let promise = new Promise((resolve, reject) => {

            let symptom = payload.symptom;

            let requestParams = {};
            requestParams.symptomCode = symptom.code;
            if (payload.edit === true) {
                requestParams.context = [contexts.EDIT];
            }
            else {
                requestParams.context = [contexts.NEXT_ENTRY];
            }

            let axiosRequest = axios.post(
                process.env.API_BASE_URL_PATHFINDER + '/assessment/chooseMainSymptomNextQ',
                requestParams
            );
            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    if (typeof response.data.data !== "undefined" && typeof response.data.data.CALLBACK_DATA !== "undefined") {
                        commit("TranslationStore/setVoucherCallbackData", response.data.data.CALLBACK_DATA, {root: true});
                    }

                    if (payload.edit === true) {
                        dispatch("setReloadedAssessmentData", response);
                    }
                    else {
                        // Add main symptom to conversation list.
                        let symptomName = (typeof symptom.name.text !== "undefined") ? symptom.name.text : symptom.name;
                        commit("appendConversationMessage", {
                            text: symptomName,
                            type: conversationMessageTypes.SENT,
                            symptomCode: symptom.code,
                            entityType: entityTypes.SYMPTOM
                        });

                        commit("clearChapterTree");

                        dispatch("goToNextQuestion", {response});
                    }

                    resolve(response);
                } else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },

    // #######################################################
    //  End CHOOSE MAIN SYMPTOM (Chapter Tree)
    // #######################################################


    // #######################################################
    //  Begin RELATED SYMPTOM
    // #######################################################

    /**
     * @TODO Maybe we have to DELETE this function, because the data that it loads should be already received via xhr request of an answer selection (on ASSESSMENT_PFI). Or do we still need it for Edit Related Symptoms popup?
     *
     * Load the list of Related Symptoms.
     * @param {Object} - Vuex context. Used properties: commit
     * @return {Promise}
     */
    loadRelatedSymptomList({commit}) {
        let promise = new Promise((resolve, reject) => {

            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/relatedSymptomList');
            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    if (typeof response.data.data !== "undefined"
                            && response.data.data !== null
                            && typeof response.data.data.RELATED_SYMPTOM_LIST !== "undefined"
                            && response.data.data.RELATED_SYMPTOM_LIST !== null
                            && typeof response.data.data.RELATED_SYMPTOM_LIST.noOtherSymptomsCheck !== "undefined") {

                        commit("setMustConfirmNoOtherSymptom", response.data.data.RELATED_SYMPTOM_LIST.noOtherSymptomsCheck);
                    }

                    if (typeof response.data.data !== "undefined"
                            && response.data.data !== null
                            && typeof response.data.data.RELATED_SYMPTOM_LIST !== "undefined"
                            && response.data.data.RELATED_SYMPTOM_LIST !== null
                            && typeof response.data.data.RELATED_SYMPTOM_LIST.symptoms !== "undefined") {

                        commit("setRelatedSymptomList", response.data.data.RELATED_SYMPTOM_LIST.symptoms);

                        resolve(response);
                    }
                    else {
                        reject("Invalid or missing related symptoms data.");
                    }
                }
                else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },


    /**
     * Tells the API about selecting a related symptom.
     * @param {Object} - Vuex context. Used properties: commit, dispatch
     * @param {Object} data - Has a "symptom" property containing selected symptom data.
     * @return {Promise}
     */
    selectRelatedSymptom({commit, dispatch}, data) {

        let promise = new Promise((resolve, reject) => {

            let requestParams = {};
            requestParams.symptomCode = data.symptom.code;
            if (typeof data.edit !== "undefined" && data.edit === true) {
                requestParams.context = [contexts.EDIT];
            }

            let axiosRequest = axios.post(
                process.env.API_BASE_URL_PATHFINDER + '/assessment/selectSymptomNextQ',
                requestParams
            );
            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    if (typeof response.data.data !== "undefined" && typeof response.data.data.CALLBACK_DATA !== "undefined") {
                        commit("TranslationStore/setVoucherCallbackData", response.data.data.CALLBACK_DATA, {root: true});
                    }

                    resolve(response);
                }
                else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },


    /**
     * Tells the API about deselecting a related symptom.
     * @param {Object} - Vuex context. Used properties: commit, dispatch
     * @param {Object} data - Has a "symptom" property containing selected symptom data.
     * @return {Promise}
     */
    deselectRelatedSymptom({commit, dispatch}, data) {

        let promise = new Promise((resolve, reject) => {

            let requestParams = {};
            requestParams.symptomCode = data.symptom.code;
            if (typeof data.edit !== "undefined" && data.edit === true) {
                requestParams.context = [contexts.EDIT];
            }

            let axiosRequest = axios.post(
                process.env.API_BASE_URL_PATHFINDER + '/assessment/deselectSymptom',
                requestParams
            );
            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    if (typeof response.data.data !== "undefined" && typeof response.data.data.CALLBACK_DATA !== "undefined") {
                        commit("TranslationStore/setVoucherCallbackData", response.data.data.CALLBACK_DATA, {root: true});
                    }

                    resolve(response);
                }
                else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },


    confirmRelatedSymptoms({commit, dispatch}, payload) {

        let promise = new Promise((resolve, reject) => {

            dispatch("submitConfirmRelatedSymptoms", payload).then((response) => {

                setTimeout(() => { // @TODO Do we still need this delay? Try to find a way to get rid of this delay.

                    if (payload && payload.selectedRelatedSymptoms.length > 0) {
                        payload.selectedRelatedSymptoms.forEach((crtSelectedRelatedSymptom) => {
                            commit("appendConversationMessage", {
                                text: crtSelectedRelatedSymptom.name,
                                type: conversationMessageTypes.SENT,
                                symptomCode: crtSelectedRelatedSymptom.code,
                                entityType: entityTypes.RELATED_SYMPTOM
                            });
                        });
                    }
                    else {
                        commit("appendConversationMessage", {
                            // text: this._vm.$text("api.chatbot.conversationMsg.noOtherSymptomsNextQ"),
                            text: this._vm.$text("api.chatbot.conversationMsg.noOtherSymptoms"),
                            type: conversationMessageTypes.SENT
                        });
                    }

                    dispatch("goToNextQuestion", {response});

                    // Clear related symptoms.
                    commit("clearRelatedSymptomList");

                    resolve(response);
                }, 650);
            });
        });

        return promise;
    },


    /**
     * Tells the server that the user has finished selecting related symptoms.
     * @param {Object} context - Vuex context. Used properties: commit, dispatch.
     * @param {Object} payload (optional) - Can contain "edit" boolean property. data.edit is true when this method isn't called when the user selects related symptoms for the first time, but when the user changes selection of related symptoms that were selected before.
     * @return {Promise}
     */
    submitConfirmRelatedSymptoms(context, payload) {
        let promise = new Promise((resolve, reject) => {

            let requestParams = {};
            if (typeof payload !== "undefined" && payload.edit === true) {
                requestParams.context = [contexts.EDIT];
            }
            else {
                requestParams.context = [contexts.NEXT_ENTRY];
            }

            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/confirmRelatedSymptoms', requestParams);
            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    if (typeof payload !== "undefined" && payload.edit === true) {
                        state.isEditAction = true;
                    }

                    resolve(response);
                } else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },

    // #######################################################
    //  End RELATED SYMPTOM
    // #######################################################


    // #######################################################
    //  Begin NO OTHER SYMPTOM
    // #######################################################

    /**
     * Tells the server that the user has confirmed that he/she doesn't have other symptoms.
     * @param {Object} - Vuex context. Used properties: commit, dispatch.
     * @return {Promise}
     */
    submitConfirmNoOtherSymptoms({commit, dispatch}, selectedAnswer) {
        let promise = new Promise((resolve, reject) => {

            let requestParams = {};
            requestParams.value = (selectedAnswer.code === noOtherSymptomAnswers.NO) ? true : false; // FALSE for "Yes" answer, TRUE for "No" answer
            requestParams.context = [contexts.NEXT_ENTRY];

            let axiosRequest = axios.post(
                process.env.API_BASE_URL_PATHFINDER + '/assessment/noOtherSymptomsNextQ',
                requestParams
            );
            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    commit("clearCurrentQuestion");

                    setTimeout(() => {  // @TODO This setTimeout is a workaround to animate the new question when answer animation is finished. Need to find a better way, it's not the best idea to handle the animation from the store.
                        dispatch("goToNextQuestion", {response});
                    }, 20); // TODO Review if is still needed this setTimeout(). I hope we can remove it.

                }
                else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },

    // #######################################################
    //  End NO OTHER SYMPTOM
    // #######################################################


    // #######################################################
    //  Begin EVALUATION / SCORING
    // #######################################################

    loadEvaluationData ({ dispatch, commit}) {
        let promise = new Promise((resolve, reject) => {

            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/evaluateAssessment');
            axiosRequest.then((response) => {
                if (typeof response.data.data !== "undefined" && typeof response.data.data.SCORING !== "undefined") {
                    // Set product list.
                    commit('setEvaluationData', response.data.data.SCORING);
                    resolve(response);
                }
                else {
                    reject("Invalid or missing evaluation data.");
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },

    loadChatBotScoringData ({ dispatch, commit}) {
        let promise = new Promise((resolve, reject) => {
            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/scoring');
            axiosRequest.then((response) => {
                if (typeof response.data.data !== "undefined" && typeof response.data.data.SCORING !== "undefined") {

                    if (typeof response.data.data.CALLBACK_DATA !== "undefined") {
                        commit("TranslationStore/setVoucherCallbackData", response.data.data.CALLBACK_DATA, {root: true});
                    }

                    // Set product list.
                    let chatbotScoringData = response.data.data.SCORING;
                    let decisionItems3 = [];
                    let decisionItems4 = [];
                    if (chatbotScoringData.decisions != null) {
                        decisionItems3 = chatbotScoringData.decisions.decisionItems3 || [];
                        decisionItems4 = chatbotScoringData.decisions.decisionItems4 || [];
                        // We separate each decisionItems3 and decisionItems4 array into 2 arrays
                        // decisionItems3Recommended, decisionItems3Possible, decisionItems4Recommended, decisionItems4Possible
                        const {decisionItems3Recommended, decisionItems3Possible, decisionItems4Recommended, decisionItems4Possible} = this._vm.$DecisionItemHelperPlugin.splitDecisionItems(decisionItems3, decisionItems4, 'mandatory');

                        // We add the 2 arrays to the scoringData.decisions object
                        chatbotScoringData.decisions['decisionItems3Recommended'] = decisionItems3Recommended;
                        chatbotScoringData.decisions['decisionItems3Possible'] = decisionItems3Possible;
                        chatbotScoringData.decisions['decisionItems4Recommended'] = decisionItems4Recommended;
                        chatbotScoringData.decisions['decisionItems4Possible'] = decisionItems4Possible;
                    }


                    commit('setChatBotScoringData', chatbotScoringData);

                    if (typeof response.data.data !== "undefined" && typeof response.data.data.ASSESSMENT !== 'undefined') {
                        if (typeof response.data.data.ASSESSMENT.assessmentToken !== 'undefined') {
                            commit('setAssessmentToken', response.data.data.ASSESSMENT.assessmentToken);
                        }
                        if (typeof response.data.data.ASSESSMENT.assessmentVersion !== 'undefined') {
                            commit('setAssessmentVersion', response.data.data.ASSESSMENT.assessmentVersion);
                        }
                    }

                    resolve(response);
                } else {
                    reject("Invalid or missing evaluation data.");
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },

    // #######################################################
    //  End EVALUATION / SCORING
    // #######################################################


    // @TODO | This function has grown big. Consider to refactor it,
    // @TODO | consider to split it, to move the code from the "switch" cases to separate functions.
    /**
    * Usually this action has to be dispatched when the server responded to an XHR request made when a question answer has been selected.
     * @param {Object} - Vuex context. Used properties: commit, dispatch, getters.
     * @param {Object} response - Axios responose object from a XHR request that has been done before this function has been called.
     * @param {Boolean} mustRefreshConversation (optional. Default: false) - If this is true, refresh all conversation messages by using conversation data received from API.
     * @return {Promise}
     */
    goToNextQuestion({dispatch, commit, getters}, {response, mustRefreshConversation}) {

        let promise = new Promise((resolve, reject) => {

            if (typeof mustRefreshConversation !== "boolean") { // Ex.: if undefined.
                mustRefreshConversation = false;
            }

            // We use this variable to make sure that when we will append new conversation messages, all the other
            // conversation messages are already set and won't overwrite the new appended messages.
            // By default, just create a Promise and resolve it immediately. But keep in mind that this can be overwritten bellow.
            let promiseConversationPreparedForAppendNewMsg = new Promise((resolve, reject) => {
                resolve();
            });

            if (response.data && response.data.assessment) {
                dispatch("AssessmentAlertsStore/refreshAlerts", response.data, { root: true });
                // @TODO Instead of this declaration of "mustShowUrgentPopup", modify refreshAlerts to return a Promise with mustShowUrgentPopup's value as payload.
                // @TODO And in order to keep Chatbot separate from the rest of the application, maybe it would be better to create here a "refreshAlerts" function to be used only in Chatbot. Have to think about it.
                let mustShowUrgentPopup = (typeof response.data.assessment.urgentData !== "undefined" && response.data.assessment.urgentData !== null) ? true : false;
                if (mustShowUrgentPopup) {
                    commit("setEndAssessment", true);
                    return; // Stop the execution. If Urgent Popup is shown, we don't have to add any more content to the page.
                }
            }

            if (!response.data || !response.data.data || !response.data.data.ASSESSMENT_PFI || !response.data.data.ASSESSMENT_PFI.conversationStatus) {
                reject("Missing assessment data.");
            }

            // If we must refresh conversation messages and we received ASSESSMENT_PFI.conversation from API, then use it.
            if (mustRefreshConversation === true && typeof response.data.data.ASSESSMENT_PFI.conversation !== "undefined") {
                promiseConversationPreparedForAppendNewMsg = dispatch("prepareConversationArr", response).then((conversation) => {
                    commit('setConversationMessages', conversation);
                });
            }

            if (response.data.assessment && response.data.assessment.assessmentToken) {
                commit('setAssessmentToken', response.data.assessment.assessmentToken); // Make sure that current assessment token is kept up to date.
            }

            commit("setCurrentQuestion", null);

            // Set the assessmentStarted flag. (Mark the assessment as being started.)
            commit("setAssessmentStarted", true); // @TODO Find out where/why exactly is this used. It seems to be related to STUDY/KSB.

            let nextAssessmentStatus = response.data.data.ASSESSMENT_PFI.conversationStatus;

            commit("setCurrentAssessmentStatus", nextAssessmentStatus);

            // If there is (are) any RECEIVED conversation message(s) that has (have) to be shown
            // before the current question, display it (them). But do it only if we didn't already
            // refresh the whole conversation list (otherwise the last messages might be duplicated).
            if (!mustRefreshConversation) {
                promiseConversationPreparedForAppendNewMsg = dispatch("prepareLastConsecutiveReceivedConvMessageArr", response).then((lastReceivedMessages) => {
                    if (lastReceivedMessages.length > 0) {
                        lastReceivedMessages.forEach((receivedConvMsg) => {
                            commit("appendConversationMessage", {
                                text: receivedConvMsg.text,
                                name: receivedConvMsg.name,
                                description: receivedConvMsg.description,
                                type: conversationMessageTypes.RECEIVED,
                            });
                        });
                    }
                });
            }

            // By using this promise, we make sure about two things regarding the conversation messages that we will
            // append in the switch/case statement:
            // - that they will not be overwritten by any unfinished prepareConversationArr() + setConversationMessages() call.
            // - that they will be appended for sure at the end of conversation messages list, AFTER
            // prepareLastConsecutiveReceivedConvMessageArr() has finished it's job.
            promiseConversationPreparedForAppendNewMsg.then(() => {

                switch (nextAssessmentStatus) {
                    case assessmentStatuses.START_QUESTIONS:
                    case assessmentStatuses.GENERAL_QUESTIONS:
                    case assessmentStatuses.SPECIFIC_QUESTIONS:
                    case assessmentStatuses.SHARED_QUESTIONS:

                        dispatch("determineNextQuestionPropertyName", nextAssessmentStatus)
                            .then((promisePayload) => {

                                let nextQuestionPropName = promisePayload;

                                dispatch("lookForNextQuestionObjInApiResp", {
                                    "response": response,
                                    "nextQuestionPropName": nextQuestionPropName
                                }).then((promisePayload) => {

                                    let nextQuestion = promisePayload;

                                    dispatch("findQuestionTextToShow", nextQuestion).then((promisePayload) => {
                                        let questionTextToShow = promisePayload;

                                        commit("appendConversationMessage", {
                                            text: questionTextToShow,
                                            type: conversationMessageTypes.RECEIVED,
                                            questionCode: nextQuestion.code,
                                            name: nextQuestion.name,
                                            description: nextQuestion.description,
                                            entityType: entityTypes.QUESTION
                                        });
                                        commit("setCurrentQuestion", nextQuestion);
                                    });
                                });
                            });

                        break;

                    // --------------

                    case assessmentStatuses.CHAPTER_TREE:

                        let chapterTreeData = null;
                        if (typeof response.data.data !== "undefined"
                                && response.data.data !== null
                                && typeof response.data.data.CHAPTER_TREE !== "undefined"
                                && response.data.data.CHAPTER_TREE !== null) {

                            chapterTreeData = response.data.data.CHAPTER_TREE;

                            commit("appendConversationMessage", {
                                text: this._vm.$text("api.chatbot.conversationMsg.selectMainSymptom"), // "What are you suffering from the most right now? Please select the main symptom.",
                                type: conversationMessageTypes.RECEIVED,
                            });
                            commit("appendConversationMessage", {
                                text: this._vm.$text("api.chatbot.conversationMsg.searchOrSelectMainSymptom"), // "You can type the symptom or search it manually.",
                                type: conversationMessageTypes.RECEIVED,
                            });

                            commit("setChapterTree", chapterTreeData);
                        }

                        break;

                    // --------------

                    case assessmentStatuses.RELATED_SYMPTOMS:

                        if (typeof response.data.data !== "undefined"
                                && response.data.data !== null
                                && typeof response.data.data.RELATED_SYMPTOM_LIST !== "undefined"
                                && response.data.data.RELATED_SYMPTOM_LIST !== null
                                && typeof response.data.data.RELATED_SYMPTOM_LIST.symptoms !== "undefined") {

                            // There are symptoms coming from the server.
                            commit("appendConversationMessage", {
                                text: this._vm.$text("api.chatbot.conversationMsg.selectRelatedSymptoms"), // "If you have other complaints, please select them.",
                                type: conversationMessageTypes.RECEIVED,
                            });

                            commit("setRelatedSymptomList", response.data.data.RELATED_SYMPTOM_LIST.symptoms);
                        }
                        else {
                            // There are NO symptoms coming from the server. But this should never happen if everything is working properly on the server.
                            // @TODO What should we do when there are no symptoms received from server? We have to decide if we just do nothing or go to NoOtherSymptoms or Specific Questions.
                        }

                        break;

                    // --------------

                    case assessmentStatuses.NO_OTHER_SYMPTOMS:
                        commit("appendConversationMessage", {
                            text: this._vm.$text("api.chatbot.conversationMsg.noOtherSymptomsQuestion"), // "Do you have any other symptoms?...",
                            type: conversationMessageTypes.RECEIVED,
                        });

                        commit("clearRelatedSymptomList");

                        dispatch("buildNoOtherSymptomQuestionObj").then((noOtherSymptomQuestionData) => {
                            commit("setCurrentQuestion", noOtherSymptomQuestionData);
                        });

                        break;

                    // --------------

                    case assessmentStatuses.EVALUATED:
                        // Do nothing, for this case is enough what we already did before this switch/case.
                        break;

                    // --------------

                    case assessmentStatuses.ASSESSMENT_STOPPED:
                        // Do nothing, for this case is enough what we already did before this switch/case.
                        break;

                    // --------------

                    default:
                        // Just do nothing, I suppose...
                }

                resolve();
            });
        });

        return promise;

    },


    // #######################################################
    //  Begin EDIT ANSWER and PAGE RELOAD.
    // #######################################################

    reloadAssessmentData({ dispatch, commit}) {

        let promise = new Promise((resolve, reject) => {
            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/assessmentPfi');
            axiosRequest.then((response) => {

                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    if (typeof response.data.data !== "undefined" && typeof response.data.data.CALLBACK_DATA !== "undefined") {
                        commit("TranslationStore/setVoucherCallbackData", response.data.data.CALLBACK_DATA, {root: true});
                    }

                    dispatch("setReloadedAssessmentData", response);
                    commit('setAssessmentStarted', true);
                    resolve(response);
                } else {
                    reject("Couldn't reload conversation.");
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;

    },


    determineNextQuestionPropertyName (context, nextAssessmentStatus) {

        let promise = new Promise((resolve, reject) => {
            let nextQuestionPropName = nextAssessmentStatus;
            if (nextQuestionPropName === "SPECIFIC_QUESTIONS") {
                nextQuestionPropName = "SYMPTOM_NEXT_Q";
            }
            else if (nextQuestionPropName === "START_QUESTIONS"
                || nextQuestionPropName === "GENERAL_QUESTIONS"
                || nextQuestionPropName === "SHARED_QUESTIONS") {
                nextQuestionPropName = nextQuestionPropName + "_NEXT_Q";
            }

            resolve(nextQuestionPropName);
        });

        return promise;
    },


    /**
     * Find in the response received from an API request the data for the question that will to be shown next.
     * @param {Object} respone - Data received as response to an API request.
     * @param nextQuestionPropName - Name of the property in response object where we must look for the next question data.
     * @return {Object} - Object with question data or NULL if the next question couldn't be found.
     */
    lookForNextQuestionObjInApiResp (context, payload) {

        let promise = new Promise((resolve, reject) => {

            let nextQuestion = null; // The last question is not included in conversation list and has to be appended now.

            if (payload.response.data.data[payload.nextQuestionPropName]) {
                if (payload.response.data.data[payload.nextQuestionPropName].nextQuestion) {
                    nextQuestion = payload.response.data.data[payload.nextQuestionPropName].nextQuestion;
                }
                else if (payload.response.data.data[payload.nextQuestionPropName].questionsData && payload.response.data.data[payload.nextQuestionPropName].questionsData.nextQuestion) {
                    nextQuestion = payload.response.data.data[payload.nextQuestionPropName].questionsData.nextQuestion;
                }
            }

            if (nextQuestion !== null) {
                resolve(nextQuestion);
            }
            else {
                reject("Couldn't find the next question.");
            }
        });

        return promise;
    },


    findQuestionTextToShow (context, questionObj) {

        let promise = new Promise((resolve, reject) => {

            let questionTextToShow = "";
            if (typeof questionObj.text !== "undefined" && questionObj.text.length > 0){
                questionTextToShow = questionObj.text;
            }
            else if (typeof questionObj.name !== "undefined" && questionObj.name.length > 0) {
                questionTextToShow = questionObj.name;
            }

            resolve(questionTextToShow);
        });

        return promise;
    },

    prepareConversationArr ({commit, dispatch}, response) {

        let promise = new Promise((resolve, reject) => {

            if (!response.data.data || !response.data.data.ASSESSMENT_PFI || !response.data.data.ASSESSMENT_PFI.conversation) {
                reject("Invalid conversation data.");
            }

            let msgList = response.data.data.ASSESSMENT_PFI.conversation;
            msgList = msgList.map((convMsg) => {
                convMsg.type = (convMsg.author === "USER") ? "SENT" : "RECEIVED";
                convMsg.questionCode = convMsg.code;
                if (convMsg.author === "USER") {
                    // @TODO Add entityType properties and corresponding code keys (depending on convMsg.status?).
                    //      Used for "id" attributes on conversation elements after the conversation is reloaded from API.
                    //      See MessageSent.vue component.
                    // ...
                }
                else if (convMsg.author === "DOCTOR" && convMsg.code) {
                    convMsg.entityType = entityTypes.QUESTION; //  Used for "id" attributes on conversation elements after the conversation is reloaded from API. See MessageReceived.vue component.
                }
                // @TODO Do we need to remove "code" and "author" properties from convMsg objects?
                return convMsg;
            });

            resolve(msgList);
        });

        return promise;
    },


    /**
     * Find last consecutive received (doctor's) conversation messages.
     * @param {Object} context - Vuex context. Used properties: none used.
     * @param {Object} response - Object containing request's response.
     * @return {Promise<array>}
     */
    prepareLastConsecutiveReceivedConvMessageArr(context, response) {

        let promise = new Promise((resolve, reject) => {

            if (!response.data.data || !response.data.data.ASSESSMENT_PFI || !response.data.data.ASSESSMENT_PFI.conversation) {
                reject("Invalid conversation data.");
            }

            let convMsgList = response.data.data.ASSESSMENT_PFI.conversation;
            let lastReceivedMessagesArr = [];

            if (convMsgList.length > 0) {
                // Loop through conversation messages, starting with the last one.
                for (let i=(convMsgList.length-1); i>=0; i--) {
                    if (convMsgList[i].author === "DOCTOR") {
                        lastReceivedMessagesArr.unshift(convMsgList[i]);
                    }
                    else {
                        // We arrived to a message that has type different than RECEIVED,
                        // so we don't need to search anymore (because we only need consecutive received messages).
                        break;
                    }
                }
            }

            resolve(lastReceivedMessagesArr);
        });

        return promise;
    },


    setReloadedAssessmentData({commit, dispatch}, response) {

        let promise = new Promise((resolve, reject) => {

            let mustRefreshConversation = true;
            dispatch("goToNextQuestion", {response, mustRefreshConversation}).then(() => {
                resolve(response);
            });

        });

        return promise;
    },




    buildNoOtherSymptomQuestionObj({commit}) {

        let promise = new Promise((resolve, reject) => {

            let capitalizeFirstLetter = (string) => {
                return string.charAt(0).toUpperCase() + string.slice(1);
            };

            // @TODO Get data from API?
            let noOtherSymptomQuestionData = {
                "text": this._vm.$text("api.chatbot.conversationMsg.noOtherSymptomsQuestion"), // "Do you have any other symptoms?...",
                "type": assessmentStatuses.NO_OTHER_SYMPTOMS,
                "providedAnswer": "",
                "answers": [
                    { "code": "YES", "text": capitalizeFirstLetter(this._vm.$text("api.label.yes")) },
                    { "code": "NO", "text": capitalizeFirstLetter(this._vm.$text("api.label.no")) }
                ]
            };
            commit("setCrtEditingQuestionData", noOtherSymptomQuestionData);

            resolve(noOtherSymptomQuestionData);
        });

        return promise;
    },

    buildNoOtherSymptomQuestionObjForEdit({commit, dispatch, getters}) {

        let promise = new Promise((resolve, reject) => {

            let promiseNoOtherSymptomsProvidedAnswer = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/getNoOtherSymptoms');
            let promiseBuiltNoOtherSymptomQuestionObj = dispatch("buildNoOtherSymptomQuestionObj");

            Promise.all([promiseNoOtherSymptomsProvidedAnswer, promiseBuiltNoOtherSymptomQuestionObj]).then((data) => {

                let response = data[0];
                let noOtherSymptomQuestionData = data[1];
                let providedNoOtherSymptomAnswer = ""; // If NoOtherSymptoms's answer wasn't already provided or if it can't be loaded from API, it's value will stay empty.

                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0
                    && typeof response.data.data.NO_OTHER_SYMPTOMS !== "undefined"
                    && typeof response.data.data.NO_OTHER_SYMPTOMS.noOtherSymptoms !== "undefined") {

                    providedNoOtherSymptomAnswer = (response.data.data.NO_OTHER_SYMPTOMS.noOtherSymptoms === true)
                        ? noOtherSymptomAnswers.NO
                        : noOtherSymptomAnswers.YES;

                    noOtherSymptomQuestionData.providedAnswer = providedNoOtherSymptomAnswer;
                    commit("setCrtEditingQuestionData", {...noOtherSymptomQuestionData});
                }

                resolve(noOtherSymptomQuestionData);
            });
        });

        return promise;
    },


    loadCrtEditingQuestion({commit}, questionCode) {
        let promise = new Promise((resolve, reject) => {

            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/getQuestion', {
                "questionCode": questionCode
            });
            axiosRequest.then((response) => {
                if (typeof response.data.data !== "undefined" && typeof response.data.data.QUESTION !== "undefined") {
                    commit("setCrtEditingQuestionData", response.data.data.QUESTION);
                    resolve(response);
                } else {
                    reject("Invalid or missing question data.");
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },


    submitEditedAnswer({commit}, data) {

        let promise = new Promise((resolve, reject) => {

            let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/provideAnswerNextQ', {
                "questionCode": data.questionCode,
                "answerCode": data.answerCode,
                "context": [contexts.EDIT]
            });
            axiosRequest.then((response) => {
                if (typeof response.data.data !== "undefined") {

                    if (typeof response.data.data.CALLBACK_DATA !== "undefined") {
                        commit("TranslationStore/setVoucherCallbackData", response.data.data.CALLBACK_DATA, {root: true});
                    }

                    // Set the flag to don't scroll to bottom new content at Edit
                    if (data.edit === true) {
                        state.isEditAction = true;
                    }

                    resolve(response);
                } else {
                    reject("Failed to submit edited answer.");
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;

    },


    /**
     * Tells the server that the user has confirmed that he/she doesn't have other symptoms.
     * @param {Object} - Vuex context. Used properties: commit, dispatch.
     * @return {Promise}
     */
    submitEditedNoOtherSymptomsAnswer({commit, dispatch}, data) {
        let promise = new Promise((resolve, reject) => {

            let requestParams = {};
            requestParams.value = (data.answerCode == "NO") ? true : false;
            requestParams.context = [contexts.EDIT];

            let axiosRequest = axios.post(
                process.env.API_BASE_URL_PATHFINDER + '/assessment/noOtherSymptomsNextQ',
                requestParams
            );
            axiosRequest.then((response) => {
                if (typeof response.data.returnCode !== "undefined" && response.data.returnCode === 0) {

                    // Set the flag to don't scroll to bottom new content at Edit
                    if (data.edit === true) {
                        state.isEditAction = true;
                    }

                    resolve(response);
                } else {
                    reject(response);
                }
            });
            axiosRequest.catch((error) => {
                console.log(error);
                reject(error);
            });
        });

        return promise;
    },

    // #######################################################
    //  End EDIT ANSWER and PAGE RELOAD.
    // #######################################################


    // #######################################################
    // Start Doc Popup
    // #######################################################

    loadDocPopupData({commit, dispatch}, data) {
        let promise = new Promise((resolve, reject) => {

            const config = {
                method: 'get',
                url: data.address,

            }
            let axiosRequest =  axios(config);

            axiosRequest.then((response) => {
                commit('setDocPopupData', {
                    title: "api.doc.pfi." + data.code,
                    content: response.data,
                });
               resolve();
            });


            axiosRequest.catch(err => {
                console.log("Error getting doc data", err);
                commit('setDocPopupData', null);
                reject(err);
            })
        })

        return promise;
    },

    updateDrSmassPersonas({commit, rootGetters}) {
        const fileRootPath = rootGetters['TranslationStore/getAppPropertyValue']('FILE_ROOT_PATH');
        const drSmassPersonas = rootGetters['TranslationStore/getAppPropertyValue']('DR_SMASS_PERSONAS');
        let drSmassPersonasArr = [];
        if (drSmassPersonas != null && drSmassPersonas !== 'NONE') {
            drSmassPersonasArr = drSmassPersonas.replace(/ /g, '').split(',');
        }
        if (fileRootPath != null && drSmassPersonasArr.length > 0) {
            const drSmassPersonasIndex = Math.floor(Math.random() * drSmassPersonasArr.length);
            commit('setDrSmassPersonas', fileRootPath + pathDrSmassPersonas + drSmassPersonasArr[drSmassPersonasIndex]);
        }
        // if NONE don't show images for personas
        else if (drSmassPersonas === 'NONE') {
            commit('setDrSmassPersonas', null);
        }
        else {
            const drSmassPersonasIndex = Math.floor(Math.random() * 2);
            const drSmassPersonasDefaultArr = ['male.png', 'female.png'];
            commit('setDrSmassPersonas', staticPathDrSmassPersonas + drSmassPersonasDefaultArr[drSmassPersonasIndex]);
        }
    }


};


// ======================================================================
// Mutations
// ======================================================================

const mutations = {

    appendConversationMessage (state, newMsg) {
        if (newMsg.text) { // If text is empty, don't add the empty message to conversation messages array.
            newMsg.status = state.currentAssessmentStatus;
            state.conversationMessages.push(newMsg);
        }
    },

    setConversationMessages(state, data) {
        state.conversationMessages = data;
    },
    clearConversationMessages(state) {
        state.conversationMessages = defaultState.conversationMessages;
    },

    setAssessmentToken (state, newValue) {
        sessionStorage.setItem('pfiAssessmentToken', newValue);
        state.assessmentToken = newValue;
    },
    clearAssessmentToken (state) {
        sessionStorage.removeItem("pfiAssessmentToken");
        state.assessmentToken = defaultState.assessmentToken;
    },

    setAssessmentVersion (state, newValue) {
        sessionStorage.setItem('pfiAssessmentVersion', newValue);
        state.assessmentVersion = newValue;
    },
    clearAssessmentVersion (state) {
        sessionStorage.removeItem("pfiAssessmentVersion");
        state.assessmentVersion = defaultState.assessmentVersion;
    },

    setProductList (state, data) {
        state.productList = data;
    },
    clearProductList (state) {
        state.productList = defaultState.productList;
    },

    setChapterTree (state, data) {
        state.chapterTree = data;
    },
    clearChapterTree (state) {
        state.chapterTree = defaultState.chapterTree;
    },
    setSearchSymptomResultList (state, data) {
        state.searchSymptomResultList = data;
    },
    clearSearchSymptomResultList (state) {
        state.searchSymptomResultList = defaultState.searchSymptomResultList;
    },

    setCurrentQuestion (state, data) {
        state.currentQuestion = data;
    },
    clearCurrentQuestion (state) {
        state.currentQuestion = defaultState.currentQuestion;
    },

    setCurrentAssessmentStatus (state, newStatus) {
        sessionStorage.setItem("crtChatbotSection", newStatus);
        state.currentAssessmentStatus = newStatus;
    },

    setRelatedSymptomList (state, data) {
        state.relatedSymptomList = data;
    },
    clearRelatedSymptomList (state) {
        state.relatedSymptomList = defaultState.relatedSymptomList;
    },

    setEvaluationData (state, data) {
        state.evaluationData = data;
    },
    setPatientFirstName (state, data) {
        state.patientFirstName = data;
    },
    setPatientLastName (state, data) {
        state.patientLastName = data;
    },
    setPatientBirthDate (state, data) {
        state.patientBirthDate = data;
    },



    /**
     * Set answer id on a Start Question.
     * @param {Object} state
     * @param {Object} data - Expected properties: "questionCode", "answerCode"
     * @return {Void}
     */
    setStartQuestionAnswer (state, data) {
        let startQuestionIndex = state.startQuestionsData.questions.findIndex((startQuestion) => {
            return startQuestion.code == data.questionCode;
        });

        if (startQuestionIndex !== -1) {
            state.startQuestionsData.questions[startQuestionIndex].providedAnswer = data.answerCode
        }
    },
    setClickedProduct (state, data) {
        state.clickedProduct = data;
    },
    setGoToLastName (state, data) {
      state.goToLastName = data;
    },
    setGoToBirthDate (state, data) {
        state.goToBirthDate = data;
    },
    setGoToGeneralQuestions (state, data) {
        state.goToGeneralQuestions = data;
    },
    setEndAssessment (state, data) {
      state.endAssessment = data;
    },
    setAssessmentStarted (state, data) {
        state.assessmentStarted = data;
    },
    setMustConfirmNoOtherSymptom (state, newValue) {
        state.mustConfirmNoOtherSymptom = newValue;
    },
    setMoreContentAvailable (state, newValue) {
        state.moreContentAvailable = newValue;
    },
    setDrSmassPersonas (state, newValue) {
        state.drSmassPersonas = newValue;
    },
    setChatBotScoringData (state, newValue) {
        state.chatBotScoringData = newValue;
    },

    // Edit answer

    setMustShowEditAnswerPopup (state, newValue) {
        state.mustShowEditAnswerPopup = newValue;
    },
    setMustShowChooseMainSymptomPopup (state, newValue) {
        state.mustShowChooseMainSymptomPopup = newValue;
    },

    setCrtEditingQuestionData (state, newData) {
        state.crtEditingQuestionData = newData;
    },
    resetCrtEditingQuestionData (state, newData) {
        state.crtEditingQuestionData = defaultState.crtEditingQuestionData;
    },

    setIsEditAction (state, newData) {
      state.isEditAction = newData;
    },

    setSearchMainSymptomInputState (state, newData) {
      state.searchMainSymptomInputState = newData;
    },

    setReloadMainSymptomValue (state, newData) {
      state.reloadMainSymptomValue = newData;
    },

    setArrowBackMainSymptomClicked (state, newValue) {
        state.arrowBackMainSymptomClicked = newValue
    },

    setExpandedChapter (state, newValue) {
      state.expandedChapter = newValue;
    },

    setEditMainSymptom (state, newValue) {
       state.editMainSymptom = newValue;
    },

    setMustShowAssessmentNoAvailablePopup (state, newValue) {
       state.mustShowAssessmentNoAvailablePopup = newValue;
    },

    setDocPopupData (state, newValue) {
        state.docPopupData = newValue
    },
    // ----------------------------------------------------------

    /**
     * Set state properties to their default values.
     * @param {Object} state
     */
    resetStore (state) {
        sessionStorage.removeItem("pfiAssessmentToken");
        state.assessmentToken             = defaultState.assessmentToken;
        state.assessmentVersion           = defaultState.assessmentVersion;
        state.currentAssessmentStatus     = defaultState.currentAssessmentStatus;
        state.conversationMessages        = [...defaultState.conversationMessages];
        state.currentQuestion             = defaultState.currentQuestion;
        state.productList                 = defaultState.productList;
        state.chapterTree                 = defaultState.chapterTree;
        state.searchSymptomResultList     = defaultState.searchSymptomResultList;
        state.relatedSymptomList          = defaultState.relatedSymptomList;
        state.evaluationData              = defaultState.evaluationData;
        state.endAssessment               = defaultState.endAssessment;
        state.assessmentStarted           = defaultState.assessmentStarted;
        state.mustConfirmNoOtherSymptom   = defaultState.mustConfirmNoOtherSymptom;
        state.moreContentAvailable        = defaultState.moreContentAvailable;
        state.drSmassPersonas                = defaultState.drSmassPersonas;
        state.chatBotScoringData          = defaultState.chatBotScoringData;
        // Edit answer
        state.mustShowEditAnswerPopup     = defaultState.mustShowEditAnswerPopup;
        state.crtEditingQuestionData      = defaultState.crtEditingQuestionData;
        state.mustShowAssessmentNoAvailablePopup = defaultState.mustShowAssessmentNoAvailablePopup;
        state.docPopupData         = defaultState.docPopupData;
    }
};


// ======================================================================
// Exports
// ======================================================================

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};



// WEBPACK FOOTER //
// ./src/common/store/modules/pathfinder/chatbot/ChatBotStore.js